// services/backupService.js
import JSZip from 'jszip';
import { saveAs } from 'file-saver';
import * as XLSX from 'xlsx';

// Configuration
const STORAGE_KEY = 'medical_platform_data';
const BACKUP_KEY = 'last_backup_date';
const BACKUP_INTERVAL_DAYS = 7; // Sauvegarde automatique tous les 7 jours

// Fonction pour obtenir la date actuelle
const getCurrentDate = () => {
  return new Date().toISOString().split('T')[0]; // Format YYYY-MM-DD
};

// Fonction pour obtenir le timestamp actuel
const getCurrentTimestamp = () => {
  return new Date().toISOString();
};

// Fonction pour vérifier si une sauvegarde est nécessaire
export const isBackupNeeded = () => {
  const lastBackupDate = localStorage.getItem(BACKUP_KEY);
  if (!lastBackupDate) {
    return true; // Première sauvegarde
  }
  
  const lastBackup = new Date(lastBackupDate);
  const today = new Date();
  const diffTime = Math.abs(today - lastBackup);
  const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
  
  return diffDays >= BACKUP_INTERVAL_DAYS;
};

// Fonction pour créer une sauvegarde complète
export const createBackup = async () => {
  try {
    const allData = JSON.parse(localStorage.getItem(STORAGE_KEY) || '{}');
    const doctorName = localStorage.getItem('doctorName') || 'Dr. Non défini';
    const timestamp = getCurrentTimestamp();
    
    // Créer un nouveau ZIP
    const zip = new JSZip();
    
    // Ajouter les données JSON brutes
    zip.file('backup_info.json', JSON.stringify({
      doctorName,
      backupDate: timestamp,
      version: '1.0',
      dataTypes: Object.keys(allData)
    }, null, 2));
    
    // Ajouter chaque type de données en Excel
    const dataFiles = {
      patients: 'Patients.xlsx',
      consultations: 'Consultations.xlsx',
      vitals: 'Constantes_Vitales.xlsx',
      treatments: 'Traitements.xlsx',
      medicalHistory: 'Antecedents_Medicaux.xlsx',
      observations: 'Observations.xlsx'
    };
    
    Object.entries(dataFiles).forEach(([dataType, filename]) => {
      if (allData[dataType] && allData[dataType].length > 0) {
        const worksheet = XLSX.utils.json_to_sheet(allData[dataType]);
        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, worksheet, 'Data');
        const excelBuffer = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });
        zip.file(filename, excelBuffer);
      }
    });
    
    // Générer le fichier ZIP
    const zipBlob = await zip.generateAsync({ type: 'blob' });
    
    // Télécharger le fichier
    const backupFilename = `AFDT-SUIVIMED-Backup-${getCurrentDate()}.zip`;
    saveAs(zipBlob, backupFilename);
    
    // Mettre à jour la date de dernière sauvegarde
    localStorage.setItem(BACKUP_KEY, timestamp);
    
    return {
      success: true,
      filename: backupFilename,
      message: 'Sauvegarde créée avec succès'
    };
    
  } catch (error) {
    console.error('Erreur lors de la création de la sauvegarde:', error);
    return {
      success: false,
      message: `Erreur lors de la sauvegarde: ${error.message}`
    };
  }
};

// Fonction pour restaurer depuis une sauvegarde
export const restoreFromBackup = async (file) => {
  try {
    const zip = new JSZip();
    const zipContent = await zip.loadAsync(file);
    
    // Lire les informations de sauvegarde
    const backupInfoFile = zipContent.file('backup_info.json');
    if (!backupInfoFile) {
      throw new Error('Fichier de sauvegarde invalide');
    }
    
    const backupInfo = JSON.parse(await backupInfoFile.async('text'));
    
    // Restaurer les données
    const restoredData = {};
    const dataFiles = {
      patients: 'Patients.xlsx',
      consultations: 'Consultations.xlsx',
      vitals: 'Constantes_Vitales.xlsx',
      treatments: 'Traitements.xlsx',
      medicalHistory: 'Antecedents_Medicaux.xlsx',
      observations: 'Observations.xlsx'
    };
    
    for (const [dataType, filename] of Object.entries(dataFiles)) {
      const file = zipContent.file(filename);
      if (file) {
        const excelBuffer = await file.async('arraybuffer');
        const workbook = XLSX.read(excelBuffer, { type: 'array' });
        const sheetName = workbook.SheetNames[0];
        const worksheet = workbook.Sheets[sheetName];
        const jsonData = XLSX.utils.sheet_to_json(worksheet);
        restoredData[dataType] = jsonData;
      }
    }
    
    // Sauvegarder les données restaurées
    localStorage.setItem(STORAGE_KEY, JSON.stringify(restoredData));
    
    // Restaurer le nom du médecin si disponible
    if (backupInfo.doctorName) {
      localStorage.setItem('doctorName', backupInfo.doctorName);
    }
    
    return {
      success: true,
      message: 'Données restaurées avec succès',
      restoredDataTypes: Object.keys(restoredData),
      backupDate: backupInfo.backupDate
    };
    
  } catch (error) {
    console.error('Erreur lors de la restauration:', error);
    return {
      success: false,
      message: `Erreur lors de la restauration: ${error.message}`
    };
  }
};

// Fonction pour obtenir les informations de la dernière sauvegarde
export const getLastBackupInfo = () => {
  const lastBackupDate = localStorage.getItem(BACKUP_KEY);
  if (!lastBackupDate) {
    return null;
  }
  
  return {
    date: lastBackupDate,
    daysAgo: Math.ceil((new Date() - new Date(lastBackupDate)) / (1000 * 60 * 60 * 24))
  };
};

// Fonction pour vérifier et proposer une sauvegarde automatique
export const checkAndProposeBackup = () => {
  if (isBackupNeeded()) {
    const lastBackupInfo = getLastBackupInfo();
    const daysAgo = lastBackupInfo ? lastBackupInfo.daysAgo : 'jamais';
    
    return {
      needed: true,
      message: `Dernière sauvegarde: ${daysAgo === 'jamais' ? 'jamais' : `il y a ${daysAgo} jours`}`,
      recommendation: 'Il est recommandé de créer une sauvegarde de vos données.'
    };
  }
  
  return {
    needed: false,
    message: 'Vos données sont à jour',
    recommendation: null
  };
};
